import {
  HospitalControllerService,
  HospitalInterviewVo,
} from '@/__generated__/FrontApi';
import ContactUs from '@/components/ContactUs';
import Container from '@/components/Container';
import Section from '@/components/Section';
import { breakpoint } from '@/helpers/BreakpointHelper';
import LayoutWithTitle from '@/layouts/LayoutWithTitle';
import { PageProps, navigate } from 'gatsby';
import React, { useCallback, useEffect, useState } from 'react';
import LinkSafe from '@/components/LinkSafe';
import styled from 'styled-components';
import Modal from '@/components/Modal';
import { BtnClose } from '@/components/Button';
import { PopCcontainer, PopupTitle } from '@/components/Css/Pop';
import { Tit } from '@/components/Titles';
import FormGroup from '@/components/Form/FormGroup';
import HospitalInquiryCard from '@/components/Card/HospitalInquiryCard';
import SwiperBasic from '@/components/Swiper/SwiperBasic';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/swiper.min.css';
import 'swiper/components/navigation/navigation.min.css';
import 'swiper/components/scrollbar/scrollbar.min.css';

import HeroImg from '@/assets/img/hospital/hero-uni926466.png';
import ProgramImg1 from '@/assets/img/hospital/program-uni677242.png';
import ProgramImg2 from '@/assets/img/hospital/program-uni621574.png';
import ProgramImg3 from '@/assets/img/hospital/program-uni765994.png';
import PlaqueBgImg from '@/assets/img/hospital/plaque-bg.png';
import PlaqueExampleImg from '@/assets/img/hospital/plaque-example.png';
import BenefitCeremonyImg from '@/assets/img/hospital/benefit-ceremony.png';
import BenefitOnlineHonorImg from '@/assets/img/hospital/benefit-online-honor.png';
import BenefitPlaqueAwardImg from '@/assets/img/hospital/benefit-plaque-award.png';
import BenefitReportImg from '@/assets/img/hospital/benefit-report.png';
import Step1Img from '@/assets/img/hospital/step-1.png';
import Step2Img from '@/assets/img/hospital/step-2.png';
import Step3Img from '@/assets/img/hospital/step-3.png';
import Step4Img from '@/assets/img/hospital/step-4.png';
import Step5Img from '@/assets/img/hospital/step-5.png';

// ============================================
// 문의 데이터
// ============================================
const contactData = [
  {
    id: 1,
    tit: `문의`,
    costumer: `기업후원2팀`,
    tel: `02-721-5154`,
    email: `csr2@unicef.or.kr`,
  },
];

// ============================================
// 사업 소개 카드 데이터
// ============================================
const programCards = [
  {
    id: 1,
    title: '기초 보건 의료 지원',
    desc: '의료 접근이 어려운 지역 어린이에게\n기초 진료, 예방접종, 감염병 대응을\n지원하여 치료받지 못해 생명을 잃는\n위험을 줄일 수 있습니다.',
    img: ProgramImg1,
    credit: '© UNICEF/UNI677242/Andriantsoarana',
  },
  {
    id: 2,
    title: '어린이 영양 지원',
    desc: '영양 부족과 영양실조에 어린이에게\n치료식과 영양 관리 지원을 통해\n건강 회복과 정상적인 성장을 지원합니다.',
    img: ProgramImg2,
    credit: '© UNICEF/UNI621574/Al-Asadi',
  },
  {
    id: 3,
    title: '긴급 보건 의료 및 대응 지원',
    desc: '재난 및 분쟁 지역에서 필요한 응급 의약품과\n의료 지원을 제공하여 위기 상황 속\n어린이의 생명을 지킬 수 있습니다.',
    img: ProgramImg3,
    credit: '© UNICEF/UNI765994/Husnain',
  },
];

// ============================================
// 가입 절차 데이터
// ============================================
const processSteps = [
  { title: '후원 상담', desc: '후원 관련 상담\n(미팅, 전화)', icon: Step1Img },
  { title: '후원 사업 선정', desc: '사업 제안서 전달\n지원 사업 분야 결정', icon: Step2Img },
  { title: '후원금 전달', desc: '후원계좌 내\n후원금 입금', icon: Step3Img },
  {
    title: '나눔병원 가입',
    desc: '기금전달식 진행\n온라인 예우공간 등재\n나눔병원 현판 제작 및 증정',
    icon: Step4Img,
  },
  { title: '사업 수행/피드백', desc: '사업 수행 및\n결과보고서 전달', icon: Step5Img },
];

// ============================================
// 예우 안내 데이터
// ============================================
const benefitItems = [
  {
    title: '나눔병원 기금전달식 진행',
    desc: '병원의 소중한 나눔을 기념합니다.',
    img: BenefitCeremonyImg,
  },
  {
    title: '온라인 예우공간 등재',
    desc: '참여하신 병원의 소중한 나눔의 가치를 기억합니다.',
    img: BenefitOnlineHonorImg,
  },
  {
    title: '나눔병원 현판 및 감사패 제공',
    desc: '깊은 감사의 마음을 담아 유니세프 나눔병원 현판과 감사패를 드립니다.',
    img: BenefitPlaqueAwardImg,
  },
  {
    title: '후원 사업보고서 전달',
    desc: '후원 사업에 대한 개별 사업보고서를 전달 드립니다.',
    img: BenefitReportImg,
  },
];

// ============================================
// Styled Components
// ============================================

/* 섹션 1: 히어로 (좌 텍스트 + 우 이미지) */
const SectionHero = styled(Section)`
  position: relative;
  overflow: hidden;

  .hero-flex {
    display: flex;
    align-items: center;
    gap: 0;
    position: relative;
    min-height: 479px;
    max-width: 1120px;
  }

  .hero-left {
    flex: 1;
    position: relative;
    z-index: 1;

    .hero-title-main {
      font-size: 48px;
      font-weight: 700;
      color: #1cabe2;
      line-height: 1.3;
    }

    .hero-title-sub {
      font-size: 30px;
      font-weight: 700;
      color: #2d2926;
      line-height: 1.3;
      margin-top: 8px;
    }

    .hero-desc-bold {
      margin-top: 32px;
      font-size: 20px;
      font-weight: 600;
      color: #2d2926;
      line-height: 1.6;
    }

    .hero-desc {
      margin-top: 16px;
      font-size: 20px;
      font-weight: 400;
      color: #2d2926;
      line-height: 1.6;

      br.mobile-only {
        display: none;
      }
    }

    .hero-btn-group {
      display: flex;
      gap: 16px;
      margin-top: 56px;
    }
  }

  .hero-btn-mobile {
    display: none;
  }

  .hero-right {
    position: relative;
    left: auto;
    width: 515px;
    flex-shrink: 0;
    top: auto;
    transform: none;
    height: 479px;
    overflow: hidden;
    margin-left: auto;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
    }

    .hero-credit {
      position: absolute;
      bottom: 10px;
      left: 10px;
      font-size: 16px;
      color: rgba(255, 255, 255, 0.5);
    }
  }

  ${breakpoint('tablet')} {
    .hero-flex {
      flex-direction: column;
      min-height: unset;
    }

    .hero-left {
      width: 100%;
      text-align: center;

      .hero-title-main {
        font-size: 28px;
      }

      .hero-title-sub {
        font-size: 24px;
      }

      .hero-desc-bold {
        font-size: 16px;
        font-weight: 700;
        line-height: 24px;

        br.desktop-only {
          display: none;
        }
      }

      .hero-desc {
        font-size: 16px;
        line-height: 26px;

        br.desktop-only {
          display: none;
        }

        br.mobile-only {
          display: block;
        }
      }

      .hero-btn-desktop {
        display: none;
      }
    }

    .hero-btn-mobile {
      display: flex;
      flex-direction: column;
      gap: 16px;
      margin-top: 32px;
      width: 100%;
    }

    .hero-right {
      position: relative;
      width: 100%;
      height: auto;
      left: auto;
      top: auto;
      transform: none;
      margin-top: 40px;
      overflow: visible;

      img {
        height: auto;
      }
    }
  }
`;

const HeroBtn = styled.button<{ variant?: 'primary' | 'outline' }>`
  width: 220px;
  height: 56px;
  border-radius: 0;
  font-size: 20px;
  font-weight: 700;
  cursor: pointer;
  transition: opacity 0.2s;

  ${({ variant }) =>
    variant === 'outline'
      ? `
    background: #fff;
    color: #1cabe2;
    border: 1px solid var(--color-azure-50, #1CABE2);
  `
      : `
    background: #1cabe2;
    color: #fff;
    border: 1px solid var(--color-azure-50, #1CABE2);
  `}

  &:hover {
    opacity: 0.85;
  }

  ${breakpoint('tablet')} {
    width: 100%;
    height: 56px;
    font-size: 20px;
    border-radius: 0;
  }
`;

/* 섹션 2: 사업 소개 (3열 카드) */
const SectionProgram = styled(Section)`
  .program-header {
    margin-bottom: 48px;
  }

  .program-title {
    font-size: 48px;
    font-weight: 700;
    color: #2d2926;
    line-height: 1.4;
    text-align: center;
  }

  .program-sub {
    margin-top: 16px;
    font-size: 18px;
    font-weight: 400;
    color: #2d2926;
    line-height: 1.3;
    text-align: center;
  }

  .program-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 32px;
    align-items: stretch;
  }

  br.mobile-only {
    display: none;
  }

  ${breakpoint('tablet')} {
    .program-title {
      font-size: 26px;
      line-height: 40px;
    }

    .program-sub {
      font-size: 16px;
      line-height: 24px;
    }

    br.mobile-only {
      display: block;
    }

    .program-grid {
      grid-template-columns: 1fr;
      gap: 48px;
    }
  }
`;

const ProgramCard = styled.div`
  display: flex;
  flex-direction: column;

  .card-img {
    width: 100%;
    height: 320px;
    overflow: hidden;
    border-radius: 0;
    position: relative;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .card-credit {
      position: absolute;
      bottom: 8px;
      right: 8px;
      font-size: 12px;
      color: rgba(255, 255, 255, 0.5);
    }
  }

  .card-body {
    padding: 40px 40px 48px;
    background: #f8f9fa;
    border-radius: 0;
    flex: 1;

    .card-title {
      font-size: 24px;
      font-weight: 700;
      color: #1cabe2;
      line-height: 1.3;
      text-align: center;
    }

    .card-desc {
      margin-top: 16px;
      font-size: 16px;
      font-weight: 400;
      color: #2d2926;
      line-height: 1.6;
      text-align: center;
      white-space: pre-line;
    }
  }

  ${breakpoint('tablet')} {
    .card-img {
      height: 320px;
    }

    .card-body {
      padding: 40px 40px 48px;
      min-height: auto;

      .card-title {
        font-size: 20px;
      }

      .card-desc {
        font-size: 14px;
        line-height: 22px;
      }
    }
  }
`;

/* 섹션 3: 현판 안내 */
const SectionPlaque = styled(Section)`
  background: #e2f0f5 url(${PlaqueBgImg}) center / cover no-repeat;

  .plaque-inner {
    display: flex;
    align-items: center;
    justify-content: space-between;
    max-width: 1120px;
    margin: 0 auto;
  }

  .plaque-left {
    width: 55%;

    .plaque-title-top {
      font-size: 48px;
      font-weight: 700;
      color: #fff;
      line-height: 1.4;
    }

    .plaque-title-bold {
      display: inline-block;
      font-size: 48px;
      font-weight: 700;
      color: #fff;
      line-height: 1.2;
      margin-top: 8px;
      background: #1cabe2;
      padding: 8px 16px;
    }

    .plaque-desc {
      margin-top: 24px;
      font-size: 18px;
      font-weight: 400;
      color: #fff;
      line-height: 1.3;
    }
  }

  .plaque-right {
    width: 40%;
    border: 9px solid #1cabe2;

    img {
      width: 100%;
      display: block;
    }

    .placeholder {
      width: 100%;
      padding-top: 65%;
      background: rgba(255, 255, 255, 0.4);
      border-radius: 0;
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }

  ${breakpoint('tablet')} {
    .plaque-inner {
      flex-direction: column;
    }

    .plaque-left {
      width: 100%;
      order: 1;
      text-align: center;

      .plaque-title-top,
      .plaque-title-bold {
        font-size: 26px;
        line-height: 40px;
      }

      .plaque-desc {
        font-size: 14px;
        line-height: 24px;
      }
    }

    .plaque-right {
      width: 100%;
      order: 2;
      margin-top: 32px;
    }
  }
`;

/* 섹션 4: 가입 절차 */
const SectionProcess = styled(Section)`

  .process-title {
    font-size: 48px;
    font-weight: 700;
    color: #2d2926;
    text-align: left;
    margin-bottom: 48px;
  }

  .process-steps {
    display: flex;
    align-items: flex-start;
    justify-content: center;
    gap: 0;
  }

  ${breakpoint('tablet')} {
    .process-title {
      font-size: 26px;
      line-height: 40px;
      margin-bottom: 48px;
      text-align: center;
    }

    .process-steps {
      display: grid;
      grid-template-columns: 1fr auto 1fr;
      gap: 32px 0;
      justify-items: center;
    }
  }
`;

const StepItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 160px;
  text-align: center;

  .step-icon {
    width: 160px;
    height: 160px;
    margin-bottom: 16px;

    img {
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
  }

  .step-title {
    font-size: 20px;
    font-weight: 700;
    color: #1cabe2;
    margin-bottom: 8px;
  }

  .step-desc {
    font-size: 16px;
    font-weight: 400;
    color: #2d2926;
    line-height: 1.5;
    white-space: pre-line;
  }

  ${breakpoint('tablet')} {
    width: auto;

    .step-icon {
      width: 80px;
      height: 80px;
    }

    .step-title {
      font-size: 16px;
      line-height: 27px;
    }

    .step-desc {
      font-size: 14px;
      line-height: 22px;
    }

    &.last-step {
      grid-column: 1 / 2;
    }
  }
`;

const StepArrow = styled.div`
  display: flex;
  align-items: center;
  padding: 0 8px;
  height: 160px;

  svg {
    width: 40px;
    height: 12px;
  }

  ${breakpoint('tablet')} {
    height: 80px;
    padding: 0 16px;

    svg {
      width: 32px;
      height: 10px;
    }

    &.row-break {
      display: none;
    }
  }
`;

/* 섹션 5: 예우 안내 (2x2) */
const SectionBenefit = styled(Section)`
  .benefit-title {
    font-size: 48px;
    font-weight: 700;
    color: #2d2926;
    margin-bottom: 48px;
  }

  .benefit-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 56px 32px;
  }

  ${breakpoint('tablet')} {
    .benefit-title {
      font-size: 26px;
      line-height: 40px;
      margin-bottom: 48px;
      text-align: center;
    }

    .benefit-grid {
      grid-template-columns: 1fr;
      gap: 40px;
    }
  }
`;

const BenefitCard = styled.div`
  .benefit-img {
    width: 100%;
    height: 280px;
    overflow: hidden;
    border-radius: 0;
    background: #f8f9fa;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .placeholder {
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 14px;
      color: #999;
    }
  }

  .benefit-body {
    margin-top: 32px;
    padding: 0 32px;

    .benefit-card-title {
      font-size: 20px;
      font-weight: 700;
      color: #1cabe2;
      line-height: 1.4;
    }

    .benefit-card-desc {
      margin-top: 8px;
      font-size: 16px;
      font-weight: 400;
      color: #2d2926;
      line-height: 1.5;
    }
  }

  ${breakpoint('tablet')} {
    .benefit-img {
      height: 180px;
    }

    .benefit-body {
      margin-top: 16px;

      .benefit-card-title {
        font-size: 16px;
        line-height: 27px;
      }

      .benefit-card-desc {
        font-size: 14px;
        line-height: 22px;
      }
    }
  }
`;

/* 섹션 6: 나눔병원 명예의 전당 (인터뷰 스와이퍼) */
const SectionHallOfFame = styled(Section)`
  .hall-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 48px;
  }

  .hall-title {
    font-size: 48px;
    font-weight: 700;
    color: #2d2926;
  }

  .hall-more {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 14px;
    font-weight: 700;
    color: #2d2926;
    text-decoration: none;

    &::after {
      content: '→';
    }
  }

  .swiper-container {
    overflow: hidden;
  }

  ${breakpoint('tablet')} {
    .hall-header {
      margin-bottom: 48px;
    }

    .hall-title {
      font-size: 26px;
      line-height: 40px;
    }
  }
`;

const HallCard = styled.div`
  a {
    display: block;
    text-decoration: none;
    color: inherit;
  }

  .hall-card-img {
    width: 100%;
    height: 256px;
    overflow: hidden;
    border-radius: 0;
    background: #f8f9fa;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .placeholder {
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 14px;
      color: #999;
    }
  }

  .hall-card-body {
    margin-top: 24px;

    .hall-card-title {
      font-size: 20px;
      font-weight: 700;
      color: #2d2926;
      line-height: 1.4;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }

    .hall-card-date {
      margin-top: 8px;
      font-size: 16px;
      font-weight: 400;
      color: #2d2926;
    }
  }

  ${breakpoint('tablet')} {
    .hall-card-img {
      height: 155px;
    }

    .hall-card-body {
      margin-top: 16px;

      .hall-card-title {
        font-size: 16px;
        line-height: 31px;
      }

      .hall-card-date {
        font-size: 14px;
        line-height: 24px;
      }
    }
  }
`;

/* 문의 */
const SectionContact = styled(Section)``;

/* 플로팅 버튼 */
const FloatingBtnWrap = styled.div`
  position: fixed;
  bottom: 50px;
  right: 40px;
  z-index: 100;
  display: flex;
  flex-direction: column;
  gap: 16px;

  body[data-mobile-menu='open'] & {
    display: none;
  }

  ${breakpoint('tablet')} {
    bottom: 0;
    left: 0;
    right: 0;
    flex-direction: row;
    gap: 0;
  }
`;

const FloatingBtn = styled.button<{ variant?: 'primary' | 'outline' }>`
  width: 150px;
  height: 78px;
  border-radius: 0;
  font-size: 18px;
  font-weight: 700;
  cursor: pointer;
  transition: opacity 0.2s;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  line-height: 1.4;

  ${({ variant }) =>
    variant === 'outline'
      ? `
    background: #fff;
    color: #1cabe2;
    border: 1px solid var(--color-azure-50, #1CABE2);
  `
      : `
    background: #1cabe2;
    color: #fff;
    border: 1px solid var(--color-azure-50, #1CABE2);
  `}

  &:hover {
    opacity: 0.85;
  }

  ${breakpoint('tablet')} {
    width: 50%;
    height: 52px;
    font-size: 16px;
    border-radius: 0;
    box-shadow: none;
    border: none;
    line-height: 1;

    br.desktop-only {
      display: none;
    }

    ${({ variant }) =>
      variant === 'outline'
        ? `
      background: #fff;
      color: #1cabe2;
      border: 1px solid #1cabe2;
      border-right: none;
    `
        : `
      border: none;
    `}
  }
`;

// ============================================
// 메인 컴포넌트
// ============================================
const HospitalSupport: React.FC<
  PageProps<GatsbyTypes.HospitalSupportPageQuery>
> = ({ location }) => {
  const [interviews, setInterviews] = useState<HospitalInterviewVo[]>([]);

  const loadInterviews = useCallback(async () => {
    try {
      const result = await HospitalControllerService.getHospitalInterviewListUsingGet();
      if (result.data) {
        setInterviews(result.data as HospitalInterviewVo[]);
      }
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => {
    loadInterviews();
  }, [loadInterviews]);

  const donateUrl = `${process.env.BACKEND_URL}/campaign/charity-hospital/donate/dn`;
  const [isInquiryModalOpen, setIsInquiryModalOpen] = useState(false);

  return (
    <LayoutWithTitle
      location={location}
      title="나눔병원"
      description="for every child, partnerships"
    >
      {/* 섹션 1: 히어로 */}
      <SectionHero className="by-sub-main-layout">
        <Container>
          <div className="hero-flex">
            <div className="hero-left">
              <div className="hero-title-main">유니세프 나눔병원</div>
              <div className="hero-title-sub">
                어린이의 생명을 지키는 병원
              </div>
              <p className="hero-desc-bold">
                병원의 나눔으로{' '}
                <br className="desktop-only" />
                전 세계 어린이의 생명을 살립니다.
              </p>
              <p className="hero-desc">
                유니세프 나눔병원은{' '}
                <br className="desktop-only" />
                연 1천만 원 이상 후원 병원과{' '}
                <br className="mobile-only" />
                함께하는
                <br className="desktop-only" />
                {' '}병원 참여형 사회공헌 프로그램입니다.
              </p>
              <div className="hero-btn-group hero-btn-desktop">
                <HeroBtn onClick={() => navigate(donateUrl)}>
                  나눔병원 후원하기
                </HeroBtn>
                <HeroBtn variant="outline" onClick={() => setIsInquiryModalOpen(true)}>
                  나눔병원 문의하기
                </HeroBtn>
              </div>
            </div>
            <div className="hero-right">
              <img src={HeroImg} alt="유니세프 나눔병원" />
              <span className="hero-credit">© UNI926466</span>
            </div>
            <div className="hero-btn-group hero-btn-mobile">
              <HeroBtn onClick={() => navigate(donateUrl)}>
                나눔병원 후원하기
              </HeroBtn>
              <HeroBtn variant="outline" onClick={() => setIsInquiryModalOpen(true)}>
                나눔병원 문의하기
              </HeroBtn>
            </div>
          </div>
        </Container>
      </SectionHero>

      {/* 섹션 2: 사업 소개 */}
      <SectionProgram className="by-sub-main-layout">
        <Container>
          <div className="program-header">
            <div className="program-title">
              유니세프 나눔병원이 되어{' '}
              <br />
              <span style={{ color: '#1cabe2' }}>
                전 세계 어린이의 생명을{' '}
                <br className="mobile-only" />
                지키는 일
              </span>
              에 함께해 주세요.
            </div>
            <p className="program-sub">
              연 1천만 원 이상의 나눔은 전 세계 어린이를 위한{' '}
              <br className="mobile-only" />
              다양한 보건·의료 지원 사업으로 이어집니다.
            </p>
          </div>
          <div className="program-grid">
            {programCards.map((card) => (
              <ProgramCard key={card.id}>
                <div className="card-img">
                  <img src={card.img} alt={card.title} />
                  <span className="card-credit">{card.credit}</span>
                </div>
                <div className="card-body">
                  <div className="card-title">{card.title}</div>
                  <p className="card-desc">{card.desc}</p>
                </div>
              </ProgramCard>
            ))}
          </div>
        </Container>
      </SectionProgram>

      {/* 섹션 3: 현판 안내 */}
      <SectionPlaque className="by-sub-main-layout">
        <Container>
          <div className="plaque-inner">
            <div className="plaque-right">
              <img src={PlaqueExampleImg} alt="나눔병원 현판예시" />
            </div>
            <div className="plaque-left">
              <div className="plaque-title-top">
                전 세계 어린이의 생명을 지키는 병원의 약속
              </div>
              <div className="plaque-title-bold">유니세프 나눔병원</div>
              <p className="plaque-desc">
                어린이의 생명을 지키는 일에 함께해 주신 병원에
                <br />
                나눔병원 현판과 감사패를 전달합니다.
              </p>
            </div>
          </div>
        </Container>
      </SectionPlaque>

      {/* 섹션 4: 가입 절차 */}
      <SectionProcess className="by-sub-main-layout">
        <Container>
          <div className="process-title">유니세프 나눔병원 가입 절차</div>
          <div className="process-steps">
            {processSteps.map((step, idx) => (
              <React.Fragment key={idx}>
                <StepItem className={idx === processSteps.length - 1 ? 'last-step' : ''}>
                  <div className="step-icon">
                    <img src={step.icon} alt={step.title} />
                  </div>
                  <div className="step-title">{step.title}</div>
                  <div className="step-desc">{step.desc}</div>
                </StepItem>
                {idx < processSteps.length - 1 && (
                  <StepArrow className={idx % 2 === 1 ? 'row-break' : ''}>
                    <svg viewBox="0 0 40 12" fill="none">
                      <path
                        d="M0 6H38M38 6L33 1M38 6L33 11"
                        stroke="#1cabe2"
                        strokeWidth="2"
                      />
                    </svg>
                  </StepArrow>
                )}
              </React.Fragment>
            ))}
          </div>
        </Container>
      </SectionProcess>

      {/* 섹션 5: 예우 안내 */}
      <SectionBenefit className="by-sub-main-layout">
        <Container>
          <div className="benefit-title">유니세프 나눔병원 예우안내</div>
          <div className="benefit-grid">
            {benefitItems.map((item, idx) => (
              <BenefitCard key={idx}>
                <div className="benefit-img">
                  <img src={item.img} alt={item.title} style={{ ...(item.objectPosition && { objectPosition: item.objectPosition }), ...(item.transform && { transform: item.transform }) }} />
                </div>
                <div className="benefit-body">
                  <div className="benefit-card-title">{item.title}</div>
                  <p className="benefit-card-desc">{item.desc}</p>
                </div>
              </BenefitCard>
            ))}
          </div>
        </Container>
      </SectionBenefit>

      {/* 섹션 6: 나눔병원 명예의 전당 (TODO: 오픈 시 false && 복원) */}
      {interviews.length > 0 && (
        <SectionHallOfFame className="by-sub-main-layout">
          <Container>
            <div className="hall-header">
              <div className="hall-title">나눔병원 명예의 전당</div>
              <LinkSafe
                to="/involve/corporate-and-organization/hospital-interview/"
                className="hall-more"
              >
                더보기
              </LinkSafe>
            </div>
            <SwiperBasic>
              <Swiper
                slidesPerView={4}
                spaceBetween={32}
                navigation
                scrollbar={{ draggable: true }}
                breakpoints={{
                  0: { slidesPerView: 2, spaceBetween: 24 },
                  641: { slidesPerView: 2, spaceBetween: 24 },
                  1025: { slidesPerView: 4, spaceBetween: 32 },
                }}
              >
                {interviews.map((item) => (
                  <SwiperSlide key={item.indexNumber}>
                    <HallCard>
                      <LinkSafe
                        to={`/involve/corporate-and-organization/hospital-interview/${item.indexNumber}`}
                      >
                        <div className="hall-card-img">
                          {item.attImgPc ? (
                            <img src={item.attImgPc} alt={item.title} />
                          ) : (
                            <div className="placeholder">이미지 영역</div>
                          )}
                        </div>
                        <div className="hall-card-body">
                          <div className="hall-card-title">{item.title}</div>
                          {item.interviewDate && (
                            <div className="hall-card-date">
                              {item.interviewDate.replace(/-/g, '.')}
                            </div>
                          )}
                        </div>
                      </LinkSafe>
                    </HallCard>
                  </SwiperSlide>
                ))}
              </Swiper>
            </SwiperBasic>
          </Container>
        </SectionHallOfFame>
      )}

      {/* 문의 */}
      <SectionContact id="sectionContact" className="by-sub-main-layout">
        <Container>
          <header>
            <h2 className="a11y">문의</h2>
          </header>
          <ContactUs itemData={contactData} />
        </Container>
      </SectionContact>

      {/* 플로팅 버튼 */}
      <FloatingBtnWrap>
        <FloatingBtn onClick={() => navigate(donateUrl)}>
          나눔병원
          <br className="desktop-only" />
          {' '}후원하기
        </FloatingBtn>
        <FloatingBtn variant="outline" onClick={() => setIsInquiryModalOpen(true)}>
          나눔병원
          <br className="desktop-only" />
          {' '}문의하기
        </FloatingBtn>
      </FloatingBtnWrap>

      {/* 나눔병원 문의하기 모달 */}
      {isInquiryModalOpen && (
        <Modal
          maxWidth="480px"
          onRequestClose={() => {}}
          style={{ zIndex: `99` }}
        >
          <PopCcontainer>
            <PopupTitle>
              <h2>
                <Tit size="s3" color="sky">
                  나눔병원 문의
                </Tit>
              </h2>
              <BtnClose onClick={() => setIsInquiryModalOpen(false)}>
                <span className="a11y">닫기</span>
              </BtnClose>
            </PopupTitle>
            <InquiryPopupContent>
              <p className="inquiry-desc">
                나눔병원에 대해
                <br />
                궁금하신 점을 문의해주세요.
              </p>
              <HospitalInquiryCard
                onComplete={(isComplete) => {
                  if (isComplete === true) setIsInquiryModalOpen(false);
                }}
              />
            </InquiryPopupContent>
          </PopCcontainer>
        </Modal>
      )}
    </LayoutWithTitle>
  );
};

const InquiryPopupContent = styled.div`
  padding: 24px 0 50px;

  .inquiry-desc {
    font-size: 16px;
    font-weight: 700;
    line-height: 1.5;
    margin-bottom: 22px;
  }

  .form-container {
    display: flex;
    flex-wrap: wrap;

    ${FormGroup} {
      width: 100%;
      padding: 0;
      margin-bottom: 24px;
    }

    .chk-label {
      font-size: 14px;
    }
  }

  textarea {
    height: 140px;
  }

  .btn-wrap {
    margin-top: 34px;

    .btn {
      width: 100%;
    }
  }
`;

export default HospitalSupport;
